import React, { useContext, useEffect, useState } from "react";
import DI from "../../utility/DI";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Skeleton,
  TextField,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HomeIcon from "@mui/icons-material/Home";
import DeleteIcon from "@mui/icons-material/Delete";
import { toTitleCase } from "../../utility/tools";
import { AppContext } from "../../../App";
import NoDataFound from "../../EmptyFallback";
import { FetchContext } from "../SideBar";

const AddHome = (props) => {
  const {
    di: { POST, GET, success, error, urls, session },
  } = props;

  const { state, setState } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [homeName, setHomeName] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [list, setList] = useState([]);
  const [members, setMembers] = useState([]);
  const [currentHome, setCurrentHome] = useState(session?.homes?.[0]?.id ?? null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [homeToDelete, setHomeToDelete] = useState(null);
  const { refresh } = useContext(FetchContext);

  // Fetch users based on search input
  const fetchUsers = async () => {
    try {
      setSearchLoading(true);
      const res = await POST(urls.get.get_friends, { search: searchInput });
      if (res?.success) {
        setList(res.data);
      } else {
        error(res?.message ?? "Error fetching users");
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setSearchLoading(false);
    }
  };

  // Add a member to the home
  const addMember = async (id) => {
    if (!id || !currentHome) {
      error("User ID and Home ID cannot be null");
      return;
    }
    try {
      setLoading(true);
      const res = await GET(urls.get.add_home_member, { user_id: id, home_id: currentHome });
      if (res?.success) {
        success(res?.message ?? "Member added successfully");
        fetchMembers();
      } else {
        error(res?.message ?? "Error adding member");
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setLoading(false);
    }
  };

  // Remove a member from the home
  const removeMember = async (id) => {
    if (!id || !currentHome) {
      error("Member ID and Home ID cannot be null");
      return;
    }
    try {
      setLoading(true);
      const res = await POST(urls.post.remove_home_members, { member_id: id, home_id: currentHome });
      if (res?.success) {
        success(res?.message ?? "Member removed successfully");
        fetchMembers();
      } else {
        error(res?.message ?? "Error removing member");
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setLoading(false);
    }
  };

  // Fetch members of the current home
  const fetchMembers = async () => {
    if (!currentHome) return;
    try {
      setLoading(true);
      const res = await GET(urls.get.get_home_members, { home_id: currentHome });
      if (res?.success) {
        setMembers(res.data);
      } else {
        error(res?.message ?? "Error fetching members");
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setLoading(false);
    }
  };

  // Fetch homes for the user
  const getHomes = async () => {
    if (!session?.id) {
      error("User ID cannot be null");
      return;
    }
    try {
      setLoading(true);
      const res = await GET(urls.get.get_homes, { user_id: session?.id });
      if (res?.success) {
        setState((prev) => ({
          ...prev,
          session: { ...prev.session, homes: res?.homes ?? [] },
        }));
        setCurrentHome(res?.homes?.[0]?.id ?? null);
      } else {
        error(res?.message ?? "Error fetching homes");
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setLoading(false);
    }
  };

  // Add a new home
  const handleAddHome = async () => {
    if (!homeName || !session?.id) {
      error("Home name and User ID cannot be null");
      return;
    }
    try {
      setLoading(true);
      const res = await POST(urls.get.create_home, { name: homeName, created_by: session?.id });
      if (res?.success) {
        success(res?.message ?? "Home added successfully");
        setHomeName("");
        await getHomes();
        fetchMembers();
      } else {
        error(res?.message ?? "Error adding home");
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setLoading(false);
    }
  };

  // Delete a home
  const handleDeleteHome = async () => {
    if (!homeToDelete || !session?.id) {
      error("Home ID and User ID cannot be null");
      return;
    }
    try {
      setLoading(true);
      const res = await POST(urls.get.delete_home, { home_id: homeToDelete.id, user_id: session?.id });
      if (res?.success) {
        success(res?.message ?? "Home deleted successfully");
        setDeleteDialogOpen(false);
        setHomeToDelete(null);

        // Fetch updated homes list using the stable get_homes API
        await getHomes();

        // If the deleted home was the current one, switch to the first available home
        if (currentHome === homeToDelete.id) {
          const updatedHomes = session?.homes ?? [];
          setCurrentHome(updatedHomes.length > 0 ? updatedHomes[0]?.id : null);
        }

        // Also trigger a refresh to ensure other components update
        refresh();
      } else {
        error(res?.message ?? "Error deleting home");
      }
    } catch {
      error("Oops..! session out. Refresh the page");
    } finally {
      setLoading(false);
    }
  };

  // Fetch members when the current home changes
  useEffect(() => {
    fetchMembers();
  }, [currentHome]);

  // Fetch homes when the refresh state changes
  useEffect(() => {
    getHomes();
  }, [refresh]);

  // Fetch users when the search input changes
  useEffect(() => {
    const timeout = setTimeout(fetchUsers, 400);
    return () => clearTimeout(timeout); // Cleanup timeout
  }, [searchInput]);

  const handleChange = (event, newValue) => {
    setCurrentHome(newValue);
  };

  return (
    <div>
      <TextField
        label="New home name?"
        value={homeName}
        onChange={(e) => setHomeName(e.target.value)}
        sx={{width:'calc(100% - 20px)', margin:'10px', marginTop:'0px'}}
      />
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddHome}
        disabled={loading || !homeName}
        sx={{marginX:'10px'}}
      >
        Add Home
      </Button>

      <Tabs
        value={currentHome}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="scrollable auto tabs example"
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        {session?.homes?.map(({ id, name }) => (
          <Tab
            key={id}
            icon={<HomeIcon />}
            iconPosition="start"
            label={name.toUpperCase()}
            value={id}
          />
        ))}
      </Tabs>

      {!session?.homes?.length ? <NoDataFound subMessage="Need to create home." /> : null}
      {currentHome != null && (
        <>
          <Autocomplete
            key={"autocomplete"}
            freeSolo
            id="free-solo-2-demo"
            loading={searchLoading}
            loadingText="Searching..."
            disableClearable
            noOptionsText={`${searchInput ? "no match found" : "not available"}`}
            sx={{ margin: "10px", marginBottom: "0px" }}
            options={list.map((option) => option.id + "#  " + option.firstname)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search new member by id or name..."
                onChange={(el) => setSearchInput(el.target.value)}
                InputProps={{
                  ...params.InputProps,
                  type: "search",
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                {...props}
                sx={{
                  borderRadius: "8px",
                  margin: "5px",
                }}
                component="li"
              >
                <div
                  key={option}
                  onClick={() => {
                    option?.split("#")?.[0] && addMember(option.split("#")[0]);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>{option}</div>
                  {!members.find((el) => el.id == option?.split?.("#")?.[0]) && (
                    <Button>
                      <PersonAddIcon />
                    </Button>
                  )}
                </div>
              </Box>
            )}
          />
          {list.length === 0 && searchInput !== "" && !searchLoading && (
            <small style={{ margin: "2px 12px", color: "grey" }}>No Search found!</small>
          )}
          <List
            sx={{ width: "100%", bgcolor: "background.paper" }}
            subheader={
              <ListSubheader>
                <p style={{ margin: "0px", fontSize: "20px", fontWeight: 600 }}>
                  Home Members ({members.length})
                </p>
              </ListSubheader>
            }
          >
            {loading ? (
              Array(5)
                .fill("t_rows")
                .map((el, index) => (
                  <ListItem key={el + index} sx={{ width: "100%" }} alignItems="flex-start">
                    <Skeleton sx={{ width: "100%" }} height={"45px"} />
                  </ListItem>
                ))
            ) : members.length === 0 ? (
              <ListItem alignItems="flex-start">
                <ListItemText primary={"No Members Exist"} />
              </ListItem>
            ) : (
              members.map((member) => (
                <React.Fragment key={member.id}>
                  <ListItem
                    sx={{ width: "100%" }}
                    alignItems="flex-start"
                    secondaryAction={
                      <IconButton edge="end" aria-label="delete" onClick={() => removeMember(member.id)}>
                        <PersonRemoveIcon color="error" />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        style={{ background: "orange" }}
                        alt={toTitleCase(member.firstname)}
                        src="/static/images/avatar/1.jpg"
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${toTitleCase(member.firstname)} ${toTitleCase(member.lastname)}`}
                      secondary={
                        <>
                          <Typography
                            sx={{ display: "inline" }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {"#" + member.id + " "} {member.email}
                          </Typography>
                        </>
                      }
                    />
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))
            )}
          </List>

          {/* Delete Home Button - Visible to all members but only enabled for home creator */}
          {session?.id && currentHome && (
            <Box sx={{ padding: "16px", textAlign: "center", width: "100%" }}>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => {
                  const home = session.homes.find(h => h.id === currentHome);
                  if (home && home.created_by === session.id) {
                    setHomeToDelete({ id: home.id, name: home.name });
                    setDeleteDialogOpen(true);
                  }
                }}
                disabled={session?.homes?.some(home => home.id === currentHome && home.created_by !== session.id)}
                fullWidth // Makes the button take full width on mobile
                sx={{
                  backgroundColor: session?.homes?.some(home => home.id === currentHome && home.created_by !== session.id)
                    ? '#cccccc'
                    : '#f44336',
                  '&:hover': {
                    backgroundColor: session?.homes?.some(home => home.id === currentHome && home.created_by !== session.id)
                      ? '#cccccc'
                      : '#d32f2f',
                  }
                }}
              >
               Delete Home: "{session.homes.find(h => h.id === currentHome)?.name}"
              </Button>
            </Box>
          )}
        </>
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Delete Home"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the home "<strong>{homeToDelete?.name}</strong>"?
            This action will permanently remove the home and all its associated expenses and members.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteHome}
            color="error"
            autoFocus
            disabled={loading}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DI(AddHome);